import React from "react";
import { Button, Form } from "react-bootstrap";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const DEFAULT_ROW_OPTIONS = [10, 25, 50];

/**
 * Shared table pagination — matches CoCM Registry design.
 * Left: Rows per page + Showing range | Right: Page nav
 */
const TablePaginationBar = ({
  currentPage,
  totalItems,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions,
  hideWhenEmpty = false,
  hideRangeLabel = false,
  className = "",
  style,
  leftExtra = null,
}) => {
  const total = Number(totalItems) || 0;
  const perPage = Number(rowsPerPage) || 10;
  const page = Number(currentPage) || 1;
  const totalPages = Math.max(1, Math.ceil(total / perPage) || 1);
  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(total, page * perPage);

  if (hideWhenEmpty && total === 0) return null;

  const canChangeRows = typeof onRowsPerPageChange === "function";
  const baseOptions =
    rowsPerPageOptions && rowsPerPageOptions.length > 0
      ? rowsPerPageOptions
      : canChangeRows
        ? DEFAULT_ROW_OPTIONS
        : [perPage];
  const options = Array.from(new Set([...baseOptions, perPage])).sort(
    (a, b) => a - b
  );

  return (
    <div
      className={`app-table-pagination d-flex justify-content-between align-items-center px-4 py-3 ${className}`}
      style={{
        borderTop: "1px solid #e9ecef",
        backgroundColor: "#fff",
        width: "100%",
        ...style,
      }}
    >
      <div
        className="d-flex align-items-center gap-3 flex-wrap"
        style={{ fontSize: "14px", color: "#575757" }}
      >
        {leftExtra}
        <span>Rows per page:</span>
        <Form.Select
          size="sm"
          value={perPage}
          disabled={!canChangeRows}
          onChange={(e) => {
            if (!canChangeRows) return;
            onRowsPerPageChange(Number(e.target.value));
          }}
          style={{
            width: "72px",
            borderRadius: "8px",
            border: "1px solid #dee2e6",
            fontSize: "13px",
            color: "#575757",
            backgroundColor: "#fff",
            cursor: canChangeRows ? "pointer" : "default",
          }}
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </Form.Select>
        {!hideRangeLabel && (
          <span className="ms-2" style={{ color: "#888" }}>
            {total === 0
              ? "Showing 0–0 of 0"
              : `Showing ${start}–${end} of ${total}`}
          </span>
        )}
      </div>

      <div className="d-flex gap-2 align-items-center flex-shrink-0">
        <Button
          variant="light"
          disabled={page <= 1}
          onClick={() => onPageChange && onPageChange(page - 1)}
          aria-label="Previous page"
          style={{
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            width: "38px",
            height: "38px",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
          }}
        >
          <FaChevronLeft size={11} color="#666" />
        </Button>
        <span
          style={{
            fontSize: "13px",
            color: "#666",
            minWidth: "80px",
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          Page {page} of {totalPages}
        </span>
        <Button
          variant="light"
          disabled={page >= totalPages || total === 0}
          onClick={() => onPageChange && onPageChange(page + 1)}
          aria-label="Next page"
          style={{
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            width: "38px",
            height: "38px",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
          }}
        >
          <FaChevronRight size={11} color="#666" />
        </Button>
      </div>
    </div>
  );
};

export default TablePaginationBar;
